import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Combobox } from '../ui/combobox';
import { Plus, Trash2 } from 'lucide-react';
import apiClient from '../../utils/api';
import { toast } from 'sonner';

const EnhancedCustomerModal = ({ isOpen, onClose, existingCustomer, onSuccess }) => {
  const [designations, setDesignations] = useState([]);
  const [rateLists, setRateLists] = useState([]);
  const [credibilityRatings, setCredibilityRatings] = useState([]);
  const [parentCustomers, setParentCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    customer_name: '',
    parent_customer_name: '',
    gst: '',
    credibility_rating: '',
    addresses: [{ address: '', city: '', state: '', email: '' }],
    contacts: [{ name: '', designation: '', mobile: '', email: '' }],
    bore_details: [{ machine_name: '', outer_diameter: '', degree: '', key_way: '', press_marks_url: '', extra_length: '' }],
    consignees: [{ consignee_name: '', address: '', city: '', state: '', gst: '' }],
    billers: [{ biller_name: '', address: '', city: '', state: '' }],
    rate_list: '',
  });

  useEffect(() => {
    if (isOpen) {
      fetchMasterData();
      if (existingCustomer) {
        setFormData({
          customer_name: existingCustomer.customer_name || existingCustomer.addresses?.[0]?.customer_name_child || '',
          parent_customer_name: existingCustomer.parent_customer_name || '',
          gst: existingCustomer.gst || existingCustomer.addresses?.[0]?.gst || '',
          credibility_rating: existingCustomer.credibility_rating || '',
          addresses: existingCustomer.addresses?.length > 0 ? existingCustomer.addresses.map(a => ({ address: a.address || '', city: a.city || '', state: a.state || '', email: a.email || '' })) : [{ address: '', city: '', state: '', email: '' }],
          contacts: existingCustomer.contacts?.length > 0 ? existingCustomer.contacts : [{ name: '', designation: '', mobile: '', email: '' }],
          bore_details: existingCustomer.bore_details?.length > 0 ? existingCustomer.bore_details : [{ machine_name: '', outer_diameter: '', degree: '', key_way: '', press_marks_url: '', extra_length: '' }],
          consignees: existingCustomer.consignees?.length > 0 ? existingCustomer.consignees.map(c => ({ consignee_name: c.consignee_name || '', address: c.address || '', city: c.city || '', state: c.state || '', gst: c.gst || '' })) : [{ consignee_name: '', address: '', city: '', state: '', gst: '' }],
          billers: existingCustomer.billers?.length > 0 ? existingCustomer.billers.map(b => ({ biller_name: b.biller_name || '', address: b.address || '', city: b.city || '', state: b.state || '' })) : [{ biller_name: '', address: '', city: '', state: '' }],
          rate_list: existingCustomer.rate_list || '',
        });
      } else {
        setFormData({
          customer_name: '',
          parent_customer_name: '',
          gst: '',
          credibility_rating: '',
          addresses: [{ address: '', city: '', state: '', email: '' }],
          contacts: [{ name: '', designation: '', mobile: '', email: '' }],
          bore_details: [{ machine_name: '', outer_diameter: '', degree: '', key_way: '', press_marks_url: '', extra_length: '' }],
          consignees: [{ consignee_name: '', address: '', city: '', state: '', gst: '' }],
          billers: [{ biller_name: '', address: '', city: '', state: '' }],
          rate_list: '',
        });
      }
    }
  }, [isOpen, existingCustomer]);

  const fetchMasterData = async () => {
    try {
      const [desResponse, rateResponse, credResponse, parentResponse] = await Promise.all([
        apiClient.get('/designations'),
        apiClient.get('/rate-lists'),
        apiClient.get('/credibility-ratings'),
        apiClient.get('/parent-customers'),
      ]);
      setDesignations(desResponse.data);
      setRateLists(rateResponse.data);
      setCredibilityRatings(credResponse.data);
      setParentCustomers(parentResponse.data);
    } catch (error) {
      toast.error('Failed to fetch master data');
    }
  };

  const addItem = (field) => {
    const templates = {
      addresses: { address: '', city: '', state: '', email: '' },
      contacts: { name: '', designation: '', mobile: '', email: '' },
      bore_details: { machine_name: '', outer_diameter: '', degree: '', key_way: '', press_marks_url: '', extra_length: '' },
      consignees: { consignee_name: '', address: '', city: '', state: '', gst: '' },
      billers: { biller_name: '', address: '', city: '', state: '' },
    };
    setFormData({ ...formData, [field]: [...formData[field], templates[field]] });
  };

  const removeItem = (field, index) => {
    const updated = formData[field].filter((_, i) => i !== index);
    if (updated.length === 0) {
      updated.push({
        addresses: { address: '', city: '', state: '', email: '' },
        contacts: { name: '', designation: '', mobile: '', email: '' },
        bore_details: { machine_name: '', outer_diameter: '', degree: '', key_way: '', press_marks_url: '', extra_length: '' },
        consignees: { consignee_name: '', address: '', city: '', state: '', gst: '' },
        billers: { biller_name: '', address: '', city: '', state: '' },
      }[field]);
    }
    setFormData({ ...formData, [field]: updated });
  };

  const updateItem = (field, index, key, value) => {
    const updated = [...formData[field]];
    updated[index][key] = value;
    setFormData({ ...formData, [field]: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        customer_name: formData.customer_name,
        parent_customer_name: formData.parent_customer_name,
        gst: formData.gst,
        credibility_rating: formData.credibility_rating,
        addresses: formData.addresses.map(a => ({ customer_name_child: formData.customer_name, address: a.address, city: a.city || null, state: a.state || null, email: a.email, gst: formData.gst })),
        contacts: formData.contacts,
        bore_details: formData.bore_details
          .filter(bd => bd.machine_name && bd.machine_name.trim() !== '')
          .map(bd => ({
            ...bd,
            outer_diameter: bd.outer_diameter ? parseFloat(bd.outer_diameter) : 0,
            degree: bd.degree ? parseFloat(bd.degree) : 0,
            extra_length: bd.extra_length ? parseFloat(bd.extra_length) : null,
          })),
        consignees: formData.consignees.map(c => ({ consignee_name: c.consignee_name, address: c.address, city: c.city || null, state: c.state || null, gst: c.gst || null })),
        billers: formData.billers.filter(b => b.biller_name && b.biller_name.trim() !== '').map(b => ({ biller_name: b.biller_name, address: b.address || null, city: b.city || null, state: b.state || null })),
        rate_list: formData.rate_list,
      };

      if (existingCustomer) {
        await apiClient.put(`/customers/${existingCustomer.id}`, payload);
        toast.success('Customer updated successfully');
      } else {
        await apiClient.post('/customers', payload);
        toast.success('Customer created successfully');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[98vw] w-full h-[98vh] max-h-[98vh] overflow-y-auto bg-[#dcdcdc]">
        <DialogHeader>
          <DialogTitle className="text-lg font-primary font-semibold">
            {existingCustomer ? 'Edit Customer' : 'Add New Customer'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="font-bold text-sm text-slate-900 bg-[#a0a0a0] -mx-6 px-6 py-2">Basic Information</h3>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Customer Name *</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  required
                  className="mt-1 h-10 bg-white"
                />
              </div>
              <div>
                <Label>Parent Name</Label>
                <div className="mt-1">
                  <Combobox
                    options={parentCustomers.map(pc => ({ value: pc.name, label: pc.name }))}
                    value={formData.parent_customer_name}
                    onValueChange={(value) => setFormData({ ...formData, parent_customer_name: value })}
                    placeholder="Select parent..."
                    searchPlaceholder="Search..."
                    className="h-10 bg-white"
                  />
                </div>
              </div>
              <div>
                <Label>GST</Label>
                <Input
                  value={formData.gst}
                  onChange={(e) => setFormData({ ...formData, gst: e.target.value })}
                  className="mt-1 h-10 bg-white"
                />
              </div>
              <div>
                <Label>Credibility Rating</Label>
                <div className="mt-1">
                  <Combobox
                    options={credibilityRatings.map(cr => ({ value: cr.rating || cr.name, label: cr.rating || cr.name }))}
                    value={formData.credibility_rating}
                    onValueChange={(value) => setFormData({ ...formData, credibility_rating: value })}
                    placeholder="Select rating..."
                    searchPlaceholder="Search..."
                    className="h-10 bg-white"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Customer Addresses */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm text-slate-900 bg-[#a0a0a0] -mx-6 px-6 py-2">Customer Addresses</h3>
              <Button
                type="button"
                size="sm"
                onClick={() => addItem('addresses')}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Address
              </Button>
            </div>
            {formData.addresses.map((addr, index) => (
              <div key={index} className="p-4 border border-slate-200 rounded-lg space-y-3 bg-slate-50">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">Address {index + 1}</span>
                  {formData.addresses.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeItem('addresses', index)} className="text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Address</Label>
                    <Input
                      value={addr.address}
                      onChange={(e) => updateItem('addresses', index, 'address', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">City</Label>
                    <Input
                      data-testid={`address-city-${index}`}
                      value={addr.city || ''}
                      onChange={(e) => updateItem('addresses', index, 'city', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">State</Label>
                    <Input
                      data-testid={`address-state-${index}`}
                      value={addr.state || ''}
                      onChange={(e) => updateItem('addresses', index, 'state', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input
                      type="email"
                      value={addr.email}
                      onChange={(e) => updateItem('addresses', index, 'email', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Customer Contacts */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm text-slate-900 bg-[#a0a0a0] -mx-6 px-6 py-2">Customer Contacts</h3>
              <Button
                type="button"
                size="sm"
                onClick={() => addItem('contacts')}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Contact
              </Button>
            </div>
            {formData.contacts.map((contact, index) => (
              <div key={index} className="p-4 border border-slate-200 rounded-lg space-y-3 bg-slate-50">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">Contact {index + 1}</span>
                  {formData.contacts.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItem('contacts', index)}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Contact Name</Label>
                    <Input
                      value={contact.name}
                      onChange={(e) => updateItem('contacts', index, 'name', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Designation</Label>
                    <Combobox
                      options={designations.map(des => ({ value: des.name, label: des.name }))}
                      value={contact.designation}
                      onValueChange={(value) => updateItem('contacts', index, 'designation', value)}
                      placeholder="Select..."
                      searchPlaceholder="Search designations..."
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Mobile Number</Label>
                    <Input
                      type="tel"
                      value={contact.mobile}
                      onChange={(e) => updateItem('contacts', index, 'mobile', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email ID</Label>
                    <Input
                      type="email"
                      value={contact.email}
                      onChange={(e) => updateItem('contacts', index, 'email', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bore Details */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm text-slate-900 bg-[#a0a0a0] -mx-6 px-6 py-2">Bore Details</h3>
              <Button
                type="button"
                size="sm"
                onClick={() => addItem('bore_details')}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Bore Detail
              </Button>
            </div>
            {formData.bore_details.map((bore, index) => (
              <div key={index} className="p-4 border border-slate-200 rounded-lg space-y-3 bg-slate-50">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">Machine {index + 1}</span>
                  {formData.bore_details.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItem('bore_details', index)}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Machine Name</Label>
                    <Input
                      value={bore.machine_name}
                      onChange={(e) => updateItem('bore_details', index, 'machine_name', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Outer Diameter (50-120mm)</Label>
                    <Input
                      type="number"
                      min="50"
                      max="120"
                      step="0.01"
                      value={bore.outer_diameter}
                      onChange={(e) => updateItem('bore_details', index, 'outer_diameter', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Degree (10-30°)</Label>
                    <Input
                      type="number"
                      min="10"
                      max="30"
                      step="0.01"
                      value={bore.degree}
                      onChange={(e) => updateItem('bore_details', index, 'degree', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Key Way</Label>
                    <Input
                      value={bore.key_way}
                      onChange={(e) => updateItem('bore_details', index, 'key_way', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Extra Length (mm)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={bore.extra_length}
                      onChange={(e) => updateItem('bore_details', index, 'extra_length', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  <div className="col-span-3">
                    <div className="flex items-center gap-2">
                      <Input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,application/pdf"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const formData = new FormData();
                            formData.append('file', file);
                            try {
                              const response = await apiClient.post('/upload', formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              updateItem('bore_details', index, 'press_marks_url', response.data.url);
                              toast.success('Press marks uploaded successfully');
                            } catch (error) {
                              toast.error('Failed to upload file');
                            }
                          }
                        }}
                        className="h-10 flex-1"
                      />
                      {bore.press_marks_url && (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}${bore.press_marks_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 text-sm underline"
                        >
                          View File
                        </a>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Press Marks (JPEG/PNG/PDF)</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Consignees */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm text-slate-900 bg-[#a0a0a0] -mx-6 px-6 py-2">Consignees</h3>
              <Button
                type="button"
                size="sm"
                onClick={() => addItem('consignees')}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Consignee
              </Button>
            </div>
            {formData.consignees.map((consignee, index) => (
              <div key={index} className="p-4 border border-slate-200 rounded-lg space-y-3 bg-slate-50">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">Consignee {index + 1}</span>
                  {formData.consignees.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItem('consignees', index)}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-5 gap-3">
                  <div>
                    <Label className="text-xs">Consignee Name</Label>
                    <Input
                      value={consignee.consignee_name}
                      onChange={(e) => updateItem('consignees', index, 'consignee_name', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Address</Label>
                    <Input
                      value={consignee.address}
                      onChange={(e) => updateItem('consignees', index, 'address', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">City</Label>
                    <Input
                      data-testid={`consignee-city-${index}`}
                      value={consignee.city || ''}
                      onChange={(e) => updateItem('consignees', index, 'city', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">State</Label>
                    <Input
                      data-testid={`consignee-state-${index}`}
                      value={consignee.state || ''}
                      onChange={(e) => updateItem('consignees', index, 'state', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">GST Number</Label>
                    <Input
                      value={consignee.gst}
                      onChange={(e) => updateItem('consignees', index, 'gst', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Billers */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm text-slate-900 bg-[#a0a0a0] -mx-6 px-6 py-2 flex-1">Billers</h3>
              <Button
                type="button"
                size="sm"
                onClick={() => addItem('billers')}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Biller
              </Button>
            </div>
            {formData.billers.map((biller, index) => (
              <div key={index} className="p-4 border border-slate-200 rounded-lg space-y-3 bg-slate-50">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">Biller {index + 1}</span>
                  {formData.billers.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItem('billers', index)}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Biller Name</Label>
                    <Input
                      value={biller.biller_name}
                      onChange={(e) => updateItem('billers', index, 'biller_name', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Address</Label>
                    <Input
                      value={biller.address}
                      onChange={(e) => updateItem('billers', index, 'address', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">City</Label>
                    <Input
                      data-testid={`biller-city-${index}`}
                      value={biller.city || ''}
                      onChange={(e) => updateItem('billers', index, 'city', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">State</Label>
                    <Input
                      data-testid={`biller-state-${index}`}
                      value={biller.state || ''}
                      onChange={(e) => updateItem('billers', index, 'state', e.target.value)}
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Rate List */}
          <div className="space-y-4">
            <h3 className="font-bold text-sm text-slate-900 bg-[#a0a0a0] -mx-6 px-6 py-2">Rate List</h3>
            <Combobox
              options={rateLists.map(rate => ({ 
                value: rate.name, 
                label: `${rate.name} (${rate.entries?.length || 0} entries)` 
              }))}
              value={formData.rate_list}
              onValueChange={(value) => setFormData({ ...formData, rate_list: value })}
              placeholder="Select Rate List"
              searchPlaceholder="Search rate lists..."
              className="h-12"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1"
            >
              {loading ? 'Saving...' : existingCustomer ? 'Update Customer' : 'Create Customer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EnhancedCustomerModal;
